import { AppointmentStatus, AppointmentType, FileType } from "~/api/API";

// Scheduling
export enum DurationMinutes {
  "30_MINUTES" = 30,
  "45_MINUTES" = 45,
  "60_MINUTES" = 60,
  "90_MINUTES" = 90,
}

const REPORT_GENERATION_SUPPORTED_APPOINTMENT_TYPES = [
  AppointmentType.QME,
  AppointmentType.QME_REEVALUATION,
  AppointmentType.AME,
  AppointmentType.AME_REEVALUATION,
  AppointmentType.SUPPLEMENTAL,
  AppointmentType.SIBTF,
  AppointmentType.SIBTF_REEVALUATION,
  AppointmentType.IME,
  AppointmentType.IME_REEVALUATION,
  AppointmentType.IME_ADR,
  AppointmentType.IME_ADR_REEVALUATION,
] as const;

export type SupportedReportGenerationAppointmentType =
  (typeof REPORT_GENERATION_SUPPORTED_APPOINTMENT_TYPES)[number];

export const isSupportedReportGenerationAppointmentType = (
  value: AppointmentType | null | undefined,
): value is SupportedReportGenerationAppointmentType => {
  return (
    !!value &&
    REPORT_GENERATION_SUPPORTED_APPOINTMENT_TYPES.includes(
      value as SupportedReportGenerationAppointmentType,
    )
  );
};

const HOI_GEN_SUPPORTED_APPOINTMENT_TYPES = [
  AppointmentType.QME,
  AppointmentType.AME,
  AppointmentType.QME_REEVALUATION,
  AppointmentType.AME_REEVALUATION,
  AppointmentType.SIBTF,
  AppointmentType.SIBTF_REEVALUATION,
  AppointmentType.IME,
  AppointmentType.IME_REEVALUATION,
  AppointmentType.IME_ADR,
  AppointmentType.IME_ADR_REEVALUATION,
] as const;

export type SupportedHoiGenAppointmentType =
  (typeof HOI_GEN_SUPPORTED_APPOINTMENT_TYPES)[number];

export const isSupportedHoiGenAppointmentType = (
  value: AppointmentType | null | undefined,
): value is SupportedHoiGenAppointmentType => {
  return (
    !!value &&
    HOI_GEN_SUPPORTED_APPOINTMENT_TYPES.includes(
      value as SupportedHoiGenAppointmentType,
    )
  );
};

/**
 * IME appointment family. Gated behind the `ime-record` flag, these render the
 * dedicated IME layout (reduced tabs, IME Appointment-tab sections, trimmed
 * claim, Plaintiff attorney, no NCM, IME report-generation modal).
 */
export const IME_APPOINTMENT_TYPES = [
  AppointmentType.IME,
  AppointmentType.IME_REEVALUATION,
  AppointmentType.IME_ADR,
  AppointmentType.IME_ADR_REEVALUATION,
] as const;

export type ImeAppointmentType = (typeof IME_APPOINTMENT_TYPES)[number];

export const isImeAppointmentType = (
  value: AppointmentType | null | undefined,
): value is ImeAppointmentType => {
  return !!value && IME_APPOINTMENT_TYPES.includes(value as ImeAppointmentType);
};

/**
 * Appointment types that represent the In-person evaluations dashboard scope.
 */
export const IN_PERSON_APPOINTMENT_TYPES: readonly AppointmentType[] = [
  AppointmentType.QME,
  AppointmentType.QME_REEVALUATION,
  AppointmentType.AME,
  AppointmentType.AME_REEVALUATION,
  AppointmentType.BRIEFING,
  AppointmentType.SIBTF,
  AppointmentType.SIBTF_REEVALUATION,
  AppointmentType.IME,
  AppointmentType.IME_REEVALUATION,
  AppointmentType.IME_ADR,
  AppointmentType.IME_ADR_REEVALUATION,
];

export interface MedicalFile {
  id: string;
  name: string;
  key: string;
  iconType: "pdf" | "video" | "xray" | "other";
  appointmentDate?: string;
  appointmentType?: AppointmentType;
  appointmentStatus?: AppointmentStatus;
  type?: FileType;
  ownerAppointmentId?: string;
  s3Prefix?: string;
}

export type ReadinessStatus = "ready" | "notReady" | "notApplicable";
